import type {Dispatch} from '@reduxjs/toolkit';
import type Keycloak from 'keycloak-js';
import api from '../store/api.ts';
import {persistor} from '../store/store.ts';
import {beginLogout, clearAuthData} from '../store/features/Auth/authSlice.ts';
import {setSelectedPhoneNumber} from '../store/features/calls/dialerSlice.ts';
import {sipUserAgent, stopSipAuthRecovery} from '../components/Dialer/services/sipService';
import {logError} from '../utils/loggerHelpers.ts';

export const performLogout = async ({
    dispatch,
    matrixLogout,
    keycloak,
}: {
    dispatch: Dispatch;
    matrixLogout: () => Promise<void>;
    keycloak: Pick<Keycloak, 'logout'>;
}): Promise<void> => {
    dispatch(beginLogout());
    window.history.replaceState({}, '', window.location.pathname);
    try {
        await matrixLogout();
    } catch (error) {
        logError('Failed to clean up Matrix during logout', {
            source: 'auth',
            event: 'matrix_logout_cleanup_failed',
            error,
        });
    }
    dispatch(api.util.resetApiState());
    dispatch(clearAuthData());
    dispatch(setSelectedPhoneNumber(undefined));
    try {
        await persistor.purge();
    } catch (error) {
        logError('Failed to clear persisted application state during logout', {
            source: 'auth',
            event: 'application_logout_storage_cleanup_failed',
            error,
        });
    }
    try {
        localStorage.removeItem("userId");
        localStorage.removeItem("tenant");
    } catch (error) {
        logError('Failed to clear persisted application state during logout', {
            source: 'auth',
            event: 'application_logout_storage_cleanup_failed',
            error,
        });
    }

    stopSipAuthRecovery();
    if (sipUserAgent) {
        try {
            await sipUserAgent.stop();
        } catch (error) {
            logError('Failed to stop SIP during logout', {
                source: 'auth',
                event: 'sip_logout_cleanup_failed',
                error,
            });
        }
    }

    try {
        await keycloak.logout();
    } catch (error) {
        logError('Failed to complete Keycloak logout', {
            source: 'auth',
            event: 'keycloak_logout_failed',
            error,
        });
    }
};
